import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import {
  InlinePlaySolidButton,
  PrimaryInfoButton,
} from 'atoms/Button/Templates'
import { IconSC } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import { LabelStyled } from 'atoms/Label'
import { OverlayLayoutStyled } from 'atoms/OverlayLayout'
import { Track } from 'atoms/ProgressBar'
import { TextBoxSC } from 'atoms/TextBox'
import {
  BaseCardSC,
  Card,
  ImageBackgroundSC,
  PosterContainerSC,
} from 'blocks/Cards'
import { Wrapper } from 'blocks/TextBlock'
import { COLOR, FIXED_INDENTS, MEDIA, TRANSITIONS } from 'vars/index'

export const ContentLabelSC = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  ${LabelStyled} {
    position: absolute;
    right: var(${FIXED_INDENTS.INDENT_30});
    bottom: var(${FIXED_INDENTS.INDENT_30});
  }

  ${Track} {
    position: absolute;
    left: 0;
    bottom: 0;
  }
`
/**
 * `description` of the series episode card (Figma `16273:212314`): the episode
 * number, the episode title and the synopsis stacked under the poster.
 */
export const DetailsTextSC = styled.div`
  display: flex;
  width: var(--cardWidth, 100%);
  max-width: var(--cardWidth, 100%);
  min-width: 0;
  box-sizing: border-box;
  flex-direction: column;
  align-items: flex-start;
  padding-right: var(${FIXED_INDENTS.INDENT_20});
  row-gap: var(${FIXED_INDENTS.INDENT_10});
  /* new/body/caption tracking: 3% of the 14px caption font size */
  letter-spacing: 0.42px;
`

export const DetailsTitleSC = styled.div`
  width: 100%;
  /* new/title/small tracking: 3% of the 16px title font size */
  letter-spacing: 0.48px;

  /* TextBox is a span with display:initial at maxLines=1, so width/ellipsis
     never apply and the episode title overflows the card (FP-4324 / Slack:
     one line). Blockify the child without changing the shared TextBox. */
  ${TextBoxSC} {
    display: block;
  }
`

/** The concept clamps the episode synopsis to three lines (Figma `16273:212314`). */
export const DETAILS_SYNOPSIS_MAX_LINES = 3

/**
 * Figma `16273:212314` (and the 1440/1024/600/375 frames) draws the synopsis
 * as a 60px three-line box: new/body/caption 0.875rem/1.429, overflow clipped,
 * ellipsis. Containment lives on this block so a long synopsis cannot grow the
 * card; `-webkit-line-clamp` on the inner TextBox span is the ellipsis only.
 */
export const DetailsSynopsisSC = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
  /* new/body/caption is 0.875rem/1.429, so three lines is the 60px of the concept */
  max-height: calc(${DETAILS_SYNOPSIS_MAX_LINES} * 1.429 * 0.875rem);

  ${TextBoxSC} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${DETAILS_SYNOPSIS_MAX_LINES};
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    max-width: 100%;
  }
`

export const PrimaryInfoButtonSC = styled(PrimaryInfoButton)``
export const InlinePlaySolidButtonSC = styled(InlinePlaySolidButton)``

export const CardChild = styled.div`
  width: 100%;
  height: 100%;
`

export const CardContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  padding: var(${FIXED_INDENTS.INDENT_30});

  ${PrimaryInfoButtonSC} {
    position: absolute;
    right: var(${FIXED_INDENTS.INDENT_30});
    top: var(${FIXED_INDENTS.INDENT_30});

    ${IconSC} {
      color: var(${COLOR.TEXT_ICON_SECONDARY});
    }
  }

  ${InlinePlaySolidButtonSC} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
  }
`
export const CardStyled = styled.div<{
  width?: string
  height?: string
}>`
  overflow: visible;
  cursor: default;
  width: ${({ width }) => (width ? `${width}` : '100%')};
  height: ${({ height }) => (height ? `${height}` : '100%')};

  ${BaseCardSC} {
    &.scale-up-110 {
      font-size: 1em;
    }

    &.scale-up-110-entered {
      svg {
        transform: scale(0.9);
        transition: transform 0.2s ease;
      }
    }

    &.scale-up-110-exiting {
      svg {
        transform: scale(0.9);
      }
    }

    &.scale-up-110-exited {
      svg {
        transform: scale(1);
        transition: transform var(${TRANSITIONS.LINEAR_400});
      }
    }
  }
`

export const CardInnerStyled = styled(Card)`
  height: ${({ aspectRatio }) =>
    `calc(var(--special-card-width) / ${aspectRatio})`};
  /* Rewrite base card styles to prevent card cutting */
  flex: auto;
`

export const CardStyledContent = styled.div<{
  viewedButtonWithHover: boolean
  viewedButtonInfo: boolean
  detailsCard?: boolean
}>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* The episodes row stretches every card to the tallest one, so distributing
     the slack pushed the text of episodes with a short or missing synopsis to
     the bottom of the card (FP-4359). The concept stacks the text right under
     the poster (Figma 18249:227740); the legacy two-line card keeps the
     space-between it was built with. */
  justify-content: ${({ detailsCard }) =>
    detailsCard ? 'flex-start' : 'space-between'};
  gap: var(${FIXED_INDENTS.INDENT_20});
  /* Make fluid margins */

  & > ${Wrapper} {
    gap: initial;

    & > :nth-child(1) {
      margin-bottom: var(${FIXED_INDENTS.INDENT_10});
    }
  }

  ${CardContent} {
    & > :nth-child(1) {
      opacity: ${({ viewedButtonInfo }) => (viewedButtonInfo ? '1' : '0')};
      z-index: ${({ viewedButtonInfo }) => (viewedButtonInfo ? '5' : '-1')};
    }

    & :nth-child(2) {
      margin: ${({ viewedButtonWithHover }) =>
        viewedButtonWithHover ? '0 auto' : '0'};
    }

    ${ButtonSC} {
      transition: opacity 150ms;

      ${MEDIA.NO_TOUCH} {
        opacity: ${({ viewedButtonWithHover }) =>
          viewedButtonWithHover ? 0 : 1};
      }

      ${MEDIA.TOUCH} {
        &:nth-child(2) {
          opacity: 0;
        }
      }
    }
  }

  ${BaseCardSC} {
    border-radius: var(${FIXED_INDENTS.INDENT_10});

    ${PosterContainerSC} {
      ${ImageBackgroundSC} {
        ${PosterSC} {
          object-fit: cover;
        }
      }
    }

    ${OverlayLayoutStyled} {
      z-index: 1;
    }
  }

  &:hover {
    ${CardContent} {
      ${ButtonSC} {
        ${MEDIA.NO_TOUCH} {
          opacity: 1;
        }
      }
    }
  }
`
